import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../components/icons';
import { Badge, Button, Field, Input, Select, Spinner } from '../components/ui';
import { Cell, DataTable, Notice, Page, PageHeader, Row, Section, Stack, StatRow }
  from '../components/Page';
import type { InvitationDto, TeamMemberDto } from '../lib/types';

/**
 * Who is in this workspace, and how somebody new gets in.
 *
 * Two of the guards here exist to stop a workspace being locked out of itself — the last
 * owner cannot be demoted or removed, and nobody can edit their own role. The server
 * enforces both; the UI states them, because a disabled control with no explanation reads
 * as a bug.
 */
export function Team() {
  const queryClient = useQueryClient();
  const { auth } = useAuth();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [issuedLink, setIssuedLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({ queryKey: ['team', 'members'], queryFn: () => api.team.members() });
  const roles = useQuery({ queryKey: ['team', 'roles'], queryFn: () => api.team.roles() });
  const invitations = useQuery({
    queryKey: ['team', 'invitations'],
    queryFn: () => api.team.invitations(),
  });

  const emailStatus = useQuery({
    queryKey: ['system', 'email'],
    queryFn: () => api.system.email(),
    staleTime: 5 * 60_000,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['team'] });
  };

  const invite = useMutation({
    mutationFn: () => api.team.invite({ email: email.trim(), role }),
    onSuccess: (result) => {
      setEmail('');
      setError(null);
      // When there is no mail provider the link comes back so it can be sent by hand.
      // Otherwise it is deliberately withheld — the recipient already has it.
      setIssuedLink(result.url ?? null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const changeRole = useMutation({
    mutationFn: ({ id, next }: { id: number; next: string }) => api.team.changeRole(id, next),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.team.remove(id),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const setPolicy = useMutation({
    mutationFn: (value: boolean) => api.team.setMfaPolicy(value),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  if (members.isLoading) return <div className="p-8"><Spinner /></div>;

  const data = members.data?.data ?? [];
  const owners = data.filter((m) => m.role === 'owner').length;
  const pending = (invitations.data?.data ?? []).filter((i) => i.status === 'Pending');
  const withoutMfa = data.filter((m) => !m.mfaEnabled).length;

  return (
    <Page width="wide">
      <PageHeader
        icon="users"
        title="Team"
        description={`Who can work in ${auth?.tenant.name ?? 'this workspace'}, and what they are allowed to do.`}
      />

      <StatRow
        items={[
          { label: 'Members', value: data.length },
          { label: 'Pending invitations', value: pending.length },
          { label: 'Owners', value: owners },
          {
            label: 'Without two-factor',
            value: withoutMfa,
            tone: withoutMfa > 0 && members.data?.requireMfa ? 'warning' : 'default',
          },
        ]}
      />

      <Stack>
        {error && <Notice tone="danger">{error}</Notice>}

        {/* ---- invite ----------------------------------------------------------- */}
        <Section
          title="Invite somebody"
          hint="They receive a single-use link tied to their email address, so forwarding it
                will not let anybody else in."
        >
        {emailStatus.data && !emailStatus.data.configured && (
          <div className="mb-3">
            <Notice tone="warning">
              No email provider is configured, so the invitation cannot be sent. The link
              will be shown here for you to pass on yourself.
            </Notice>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[16rem] flex-1">
            <Field label="Email address">
              <Input
                type="email"
                value={email}
                placeholder="colleague@yourcompany.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
          </div>

          <Field label="Role">
            <Select value={role} onChange={(e) => setRole(e.target.value)}>
              {(roles.data?.data ?? []).map((r) => (
                <option key={r.key} value={r.key}>{r.name}</option>
              ))}
            </Select>
          </Field>

          <Button
            variant="primary"
            onClick={() => invite.mutate()}
            disabled={invite.isPending || !email.includes('@')}
          >
            {invite.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </div>

        {issuedLink && (
          <div className="mt-3 rounded-md border border-[var(--ff-border)]
                          bg-[var(--ff-surface-sunken)] p-3">
            <p className="mb-2 text-xs text-[var(--ff-text-muted)]">
              Send this link to them yourself. It works once, for that address only.
            </p>
            <div className="flex gap-2">
              <input
                readOnly
                value={issuedLink}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded border border-[var(--ff-border)]
                           bg-[var(--ff-surface)] p-2 font-mono text-xs"
              />
              <Button onClick={() => void navigator.clipboard.writeText(issuedLink)}>
                <Icon name="copy" size={14} /> Copy
              </Button>
            </div>
          </div>
        )}

        {roles.data && (
          <p className="mt-3 text-xs text-[var(--ff-text-muted)]">
            {roles.data.data.find((r) => r.key === role)?.description
              ?? `${roles.data.data.find((r) => r.key === role)?.permissions.length ?? 0} permissions.`}
          </p>
        )}
        </Section>

        {/* ---- pending invitations --------------------------------------------- */}
        {pending.length > 0 && (
          <Section
            title="Pending invitations"
            hint="Resending issues a new link, because only a hash of the old one was kept —
                  so the earlier email stops working."
          >
            <ul className="space-y-2">
              {pending.map((i) => (
                <InvitationRow key={i.id} invitation={i} onChanged={refresh} onError={setError} />
              ))}
            </ul>
          </Section>
        )}

        {/* ---- members ---------------------------------------------------------- */}
        <DataTable headers={['Member', 'Role', 'Two-factor', 'Joined', '']}>
          {data.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              roles={roles.data?.data ?? []}
              isLastOwner={member.role === 'owner' && owners <= 1}
              onChangeRole={(next) => changeRole.mutate({ id: member.id, next })}
              onRemove={() => remove.mutate(member.id)}
            />
          ))}
        </DataTable>

        {/* ---- policy ----------------------------------------------------------- */}
        <Section
          title="Require two-factor authentication"
          tone={members.data?.requireMfa ? 'default' : 'danger'}
          hint={
            <>
              Everyone in this workspace must have an authenticator set up before they can
              sign in.{' '}
              {withoutMfa > 0 && (
                <strong className="text-[var(--ff-warning)]">
                  {withoutMfa} member{withoutMfa === 1 ? '' : 's'} would be locked out until
                  they enrol.
                </strong>
              )}
            </>
          }
          actions={
            <Button
              variant={members.data?.requireMfa ? 'secondary' : 'primary'}
              onClick={() => setPolicy.mutate(!members.data?.requireMfa)}
              disabled={setPolicy.isPending}
            >
              {members.data?.requireMfa ? 'Turn off' : 'Require it'}
            </Button>
          }
        >
          <p className="text-sm text-[var(--ff-text-muted)]">
            {members.data?.requireMfa
              ? 'Required. Members without an authenticator cannot sign in to this workspace.'
              : 'Not required. Members can choose whether to set one up.'}
          </p>
        </Section>
      </Stack>
    </Page>
  );
}

function MemberRow({
  member, roles, isLastOwner, onChangeRole, onRemove,
}: {
  member: TeamMemberDto;
  roles: { key: string; name: string }[];
  isLastOwner: boolean;
  onChangeRole: (role: string) => void;
  onRemove: () => void;
}) {
  // Both are server-enforced. Surfacing the reason here stops a disabled control reading
  // as a broken one.
  const locked = member.isSelf || isLastOwner;
  const reason = member.isSelf
    ? 'You cannot change your own role or remove yourself.'
    : 'This is the only owner. Make somebody else an owner first.';

  return (
    <Row>
      <Cell>
        <div className="font-medium">{member.displayName ?? member.email}</div>
        <div className="text-xs text-[var(--ff-text-muted)]">
          {member.email}
          {!member.emailVerified && (
            <span className="ml-1.5 text-[var(--ff-warning)]">unverified</span>
          )}
        </div>
      </Cell>

      <Cell>
        {locked ? (
          <span title={reason} className="flex items-center gap-1.5">
            <Badge tone={member.role === 'owner' ? 'primary' : 'neutral'}>{member.role}</Badge>
            <Icon name="info" size={13} />
          </span>
        ) : (
          <Select
            value={member.role}
            aria-label={`Role for ${member.email}`}
            onChange={(e) => onChangeRole(e.target.value)}
          >
            {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
          </Select>
        )}
      </Cell>

      <Cell>
        {member.mfaEnabled
          ? <Badge tone="success">on</Badge>
          : <span className="text-xs text-[var(--ff-text-muted)]">off</span>}
      </Cell>

      <Cell muted nowrap>
        {member.joinedUtc ? new Date(member.joinedUtc).toLocaleDateString() : '—'}
      </Cell>

      <Cell>
        <button
          type="button"
          disabled={locked}
          title={locked ? reason : `Remove ${member.email}`}
          aria-label={`Remove ${member.email}`}
          onClick={onRemove}
          className="rounded p-1 text-[var(--ff-danger)] hover:bg-[var(--ff-danger-soft)]
                     disabled:pointer-events-none disabled:opacity-30"
        >
          <Icon name="trash" size={15} />
        </button>
      </Cell>
    </Row>
  );
}

function InvitationRow({
  invitation, onChanged, onError,
}: {
  invitation: InvitationDto;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const resend = useMutation({
    mutationFn: () => api.team.resendInvitation(invitation.id),
    onSuccess: onChanged,
    onError: (e: Error) => onError(e.message),
  });

  const revoke = useMutation({
    mutationFn: () => api.team.revokeInvitation(invitation.id),
    onSuccess: onChanged,
    onError: (e: Error) => onError(e.message),
  });

  return (
    <li className="flex items-center gap-3 rounded-md border border-[var(--ff-border)] p-2.5">
      <Icon name="mail" size={15} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm">{invitation.email}</div>
        <div className="text-xs text-[var(--ff-text-muted)]">
          {invitation.role} · expires {new Date(invitation.expiresUtc).toLocaleDateString()}
          {invitation.invitedByName && ` · invited by ${invitation.invitedByName}`}
        </div>
      </div>

      {/* Resending necessarily issues a new token, because only the hash of the old one
          was kept — so the earlier email stops working. */}
      <Button onClick={() => resend.mutate()} disabled={resend.isPending}>
        {resend.isPending ? 'Sending…' : 'Resend'}
      </Button>

      <Button variant="danger" onClick={() => revoke.mutate()} disabled={revoke.isPending}>
        Revoke
      </Button>
    </li>
  );
}
